import React, { useState, useEffect, useMemo } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import { ZustandStore } from "../../../store/zustand-store";
import { DELETE_USER_HISTORY } from "../../../services/constants";
import axios from "axios";
import { Box } from "@mui/material";
import { GET_USER_HISTORY } from "../../../services/constants";
import { useDispatch } from "react-redux";

import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TablePagination } from "@mui/material";
import VirtualizeTable from "../../share/tables/DynamicTable/VirtualizeTable";
import { useTranslation } from "react-i18next";

const History = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ItemsPerPage = 50;
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const admin_mail = localStorage.getItem("admin_mail");

  const filter_data = useMemo(() => {
    if (data?.length === 0) return [];
    if (type) {
      return data?.filter((item) => {
        return item.type === type;
      });
    } else {
      return data;
    }
  }, [data, type]);

  const fetchData = async () => {
    try {
      const response = await axios.get(GET_USER_HISTORY(admin_mail));
      setData(response.data);
      setLoading(false);
    } catch (error) {
      setData([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounceFetch);
  }, [admin_mail]);

  useEffect(() => {
    if (page > Math.max(0, Math.ceil(filter_data.length / ItemsPerPage) - 1)) {
      setPage(Math.max(0, Math.ceil(filter_data.length / ItemsPerPage) - 1));
    }
  }, [filter_data, page]);

  const tableHeader = [
    {
      label: t("activity.header.label_1"),
      dataKey: "search",
      minWidth: "250px",
      align: "left",
      width: "25%",
    },

    {
      label: t("activity.header.label_2"),
      dataKey: "type",
      minWidth: "100px",
      align: "center",
      width: "25%",
    },
    {
      label: t("activity.header.label_3"),
      dataKey: "last_seen",
      minWidth: "100px",
      align: "center",
      width: "25%",
    },

    {
      label: "",
      dataKey: "space",
      minWidth: "60px",
      align: "center",
      width: "25%",
    },
  ];

  const handleHistoryRedirect = (type, item) => {
    if (type === "backlinks" || type === "gap") {
      ZustandStore.setState({
        get_domain_authority: [],
        backlink_analysis: { data: [], type: "" },
      });
      dispatch({ type: "BACKLINKDOMAIN", payload: item?.data });
      navigate("/backlinks");
    } else if (type === "keyword") {
      ZustandStore.setState({
        keyword_research_data: {
          data: [],
          loading: false,
          search: false,
          error: false,
        },
        keyword_ques_based_data: {
          data: [],
          loading: false,
          search: false,
        },
        keyword_research_ideas_data: {
          data: [],
          loading: false,
          search: false,
        },
      });
      sessionStorage.setItem("KEYWORD_DOMAIN_NAME", item.data);
      sessionStorage.setItem(
        "KEYWORD_DOMAIN_COUNTRY",
        JSON.stringify(item.location)
      );
      navigate("/keyword-research/overview");
    } else {
      ZustandStore.setState({
        worldwide_domain: null,
        site_overview_worldwide: {
          domain: "",
          data: [],
        },
        site_overview_backlink_data: [],
        site_overview_page_data: [],
      });
      const domainLocation = {
        location_name: item?.location.location_name,
        location_code: item?.location.location_code,
        location_flag: item?.location.location_flag,
        language_code: item?.location.language_code,
        language_name: item?.location?.language_name,
        show: item.data === "Worldwide" ? "worldwide" : "",
      };
      sessionStorage.setItem("lastdomainname", item.data);
      sessionStorage.setItem("domainCountry", JSON.stringify(domainLocation));
      navigate("/site-overview");
    }
  };

  const handleDeleteHistory = async (item) => {
    setLoading(true);
    try {
      await axios.delete(DELETE_USER_HISTORY(), {
        data: {
          email: admin_mail,
          data: item.data,
          type: item.type,
          location: item.location === 0 ? "0" : JSON.stringify(item.location),
        },
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting history:", error);
      alert("Failed to delete the history. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const rowData = useMemo(() => {
    if (filter_data?.length === 0) return [];
    return filter_data?.slice(
      page * ItemsPerPage,
      page * ItemsPerPage + ItemsPerPage
    );
  }, [filter_data, page, ItemsPerPage]);

  const rows = rowData?.map((item) => {
    return {
      search: (
        <div
          style={{ width: "fit-content", color: "#365486" }}
          className="point"
          onClick={() => handleHistoryRedirect(item.type, item)}
        >
          {item?.data}
          <OpenInNewIcon
            fontSize="small"
            style={{ color: "#365486", fontSize: "1em" }}
            className="ms-1"
          />
        </div>
      ),
      type: (
        <span>
          {item?.type === "keyword"
            ? "Keyword Research"
            : item.type === "site"
            ? "Site-Overview"
            : "Backlinks analysis"}
        </span>
      ),
      last_seen: <span>{`${item.date}-${item.month}`}</span>,
      space: (
        <div
          className="d-flex align-items-center justify-content-end"
          onClick={() => handleDeleteHistory(item)}
        >
          <DeleteIcon style={{ color: "#6254FF", cursor: "pointer" }} />
        </div>
      ),
    };
  });

  const handleChangePage = (event, newPage) => {
    const maxPage = Math.max(
      0,
      Math.ceil(filter_data?.length / ItemsPerPage) - 1
    );
    setPage(Math.min(Math.max(newPage, 0), maxPage)); // Clamp page between 0 and maxPage
  };

  function fixedHeaderContent() {
    return (
      <TableRow>
        {tableHeader.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.align}
            className="font-medium"
            sx={{
              fontSize: "0.9em",
              color: "#191B23",
              backgroundColor: "#F4F5F9",
              padding: "10px",
              width: column.minWidth,
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function rowContent(_index, row) {
    return (
      <React.Fragment>
        {tableHeader?.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.align}
            className="font-medium"
            sx={{
              fontSize: "1em",
              color: "#191B23",
              padding: "10px",
            }}
          >
            {row[column.dataKey]}
          </TableCell>
        ))}
      </React.Fragment>
    );
  }

  return (
    <section className="mt-3">
      <Box className="mb-3 d-flex flex-wrap align-items-center">
        <h2 className="font-bold heading-large mb-0">{t("activity.title")}</h2>
      </Box>
      <Paper
        className={"w-100 my-3 vir-table-border"}
        sx={{
          backgroundColor: "background.paper",
          boxShadow: "none",
          "& .MuiTableContainer-root": {
            boxShadow: "none",
            borderRadius: 0,
            backgroundColor: "background.paper",
          },
        }}
      >
        <VirtualizeTable
          {...{
            loading,
            rowContent,
            data: rows || [],
            fixedHeaderContent,
            columns: tableHeader,
            columnLength: tableHeader?.length,
          }}
        />
        {!loading && (
          <TablePagination
            sx={{
              borderTop: "1px solid rgba(224, 224, 224, 1)",
              cursor: "default",
              backgroundColor: "background.paper",
              "& .MuiTablePagination-actions": {
                width: 90,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              },
              "& .MuiTablePagination-displayedRows": {
                padding: 0,
                margin: 0,
              },
              "& .MuiTablePagination-selectLabel": {
                padding: 0,
                margin: 0,
              },
            }}
            component="div"
            disabled={filter_data?.length === 0}
            count={filter_data?.length}
            page={page}
            rowsPerPageOptions={[25]}
            rowsPerPage={ItemsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={() => {}}
          />
        )}
      </Paper>
    </section>
  );
};

export default History;
